import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Alert, Box, Typography, Button } from '@mui/material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { FaceMesh } from '@mediapipe/face_mesh';
import SignalsPanel from '../components/SignalsPanel';
import HealthCard from '../components/HealthCard';

// Same face-tracking overlay WebcamCard draws during live monitoring (oval
// guide + landmark dots), copied rather than imported — WebcamCard's version
// is wired into the live inference/recording path, and duplicating this
// self-contained drawing function here avoids any risk of touching that
// production code path for a demo-only page.
function drawFaceGuideOval(
  ctx: CanvasRenderingContext2D,
  W: number,
  H: number,
  faceLandmarks: { x: number; y: number }[] | null,
) {
  const cx = W * 0.5;
  const cy = H * 0.43;
  const rx = W * 0.22;
  const ry = Math.min(H * 0.34, rx * 1.5);

  let borderColor = 'rgba(255,255,255,0.75)';
  let glowColor = 'transparent';

  if (faceLandmarks) {
    const xs = faceLandmarks.map((lm) => lm.x * W);
    const ys = faceLandmarks.map((lm) => lm.y * H);
    const faceCx = (Math.min(...xs) + Math.max(...xs)) / 2;
    const faceCy = (Math.min(...ys) + Math.max(...ys)) / 2;
    const faceW = Math.max(...xs) - Math.min(...xs);
    const faceH = Math.max(...ys) - Math.min(...ys);
    const avgFill = (faceW / (2 * rx) + faceH / (2 * ry)) / 2;
    const centered = Math.abs(faceCx - cx) < W * 0.15 && Math.abs(faceCy - cy) < H * 0.15;

    if (centered && avgFill >= 0.65 && avgFill <= 1.3) {
      borderColor = 'rgba(74,222,128,0.98)';
      glowColor = 'rgba(74,222,128,0.45)';
    } else {
      borderColor = 'rgba(96,165,250,0.95)';
    }
  }

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, W, H);
  ctx.ellipse(cx, cy, rx, ry, 0, Math.PI * 2, 0, true);
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 3;
  if (glowColor !== 'transparent') {
    ctx.shadowColor = glowColor;
    ctx.shadowBlur = 14;
  }
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.restore();
}

// Replays a saved, offline-computed inference run (generate_demo.py in
// bp-back/healthcheck) through the exact same display components the live
// app uses — SignalsPanel's queue+requestAnimationFrame drain, the exact
// buffer_frames diffing — just fed by a static JSON array paced by video
// playback time instead of live WebSocket messages. The inference itself
// is 100% offline/precomputed; nothing here calls the model.
interface ReplayResult {
  PPG: number[]; ECG: number[]; ICG: number[]; BP_wave: number[];
  HR: number | null; RR: number | null;
  SBP: number[] | null; DBP: number[] | null; MAP: number | null; SpO2: number | null;
  buffer_frames: number; _t: number;
}

// Real lab-device reference traces for a held-out verification subject (see
// bp-back/healthcheck/verification_demos/build_demo.py) — full-clip
// continuous arrays, pre-aligned by that script to the same 30fps timeline
// buffer_frames already indexes into, so no alignment math is needed here;
// `corr` is that script's own lag-searched correlation per channel against
// the model's output, shown as a number alongside the visual overlay.
interface GroundTruthMetrics { hr?: number | null; sbp?: number | null; dbp?: number | null; map?: number | null }

interface GroundTruth {
  ppg: number[]; ecg: number[]; icg: number[]; bp: number[]; fps: number;
  corr?: { ppg?: number; ecg?: number; icg?: number; bp?: number };
  label?: string;
  // Whole-clip fallback value (the series' own last/most-complete entry) --
  // kept for any caller that just wants one number.
  metrics?: GroundTruthMetrics;
  // The actual comparison: a rolling-window trajectory over the clip,
  // stepped once per second, computed with the same window lengths
  // inference_service.py itself uses (20s for HR, 60s for BP) -- so this
  // updates live during playback just like the model's own HR/BP do,
  // instead of sitting on one static number while the model's moves. See
  // build_demo.py's compute_metrics_series.
  metrics_t?: number[];
  metrics_series?: { hr: (number | null)[]; sbp: (number | null)[]; dbp: (number | null)[]; map: (number | null)[] };
}

// With no :demoId, this page replays the original reference-video dataset
// (no ground truth available for that one — it's the healthcheck demo video,
// not a verification subject with a lab-device recording). With a :demoId,
// it replays one of the verification demos, each with its own clip + real
// ground truth, committed under public/demo-replay/{demoId}/.
const DEFAULT_DATA_URL = '/demo-replay/full_results.json';
// Same origin as this app (app.seevita.ai), just a different path — served by
// the GPU box's health check static mount (bp-back/healthcheck), not bundled
// into this app's own build. *.mp4 is gitignored here (see .gitignore) and
// CI's checkout wouldn't have a bundled copy anyway, so this avoids silently
// 404ing in production rather than fighting that convention.
const DEFAULT_VIDEO_URL = '/healthcheck/reference_preview.mp4';

// Nearest-timestamp lookup into a GroundTruth's metrics_t/metrics_series --
// same pattern as bp-back's test_hr_accuracy.py's own nearest_hr, just in
// TypeScript. Half the step size (compute_metrics_series steps once per
// second) is a tight enough tolerance to avoid ever matching the wrong
// second, generous enough to tolerate float rounding in r._t.
function nearestMetrics(
  metricsT: number[],
  series: NonNullable<GroundTruth['metrics_series']>,
  targetT: number,
  toleranceS = 0.6,
): GroundTruthMetrics | undefined {
  let bestIdx = -1;
  let bestDt = toleranceS;
  for (let i = 0; i < metricsT.length; i++) {
    const dt = Math.abs(metricsT[i] - targetT);
    if (dt <= bestDt) { bestDt = dt; bestIdx = i; }
  }
  if (bestIdx === -1) return undefined;
  return { hr: series.hr[bestIdx], sbp: series.sbp[bestIdx], dbp: series.dbp[bestIdx], map: series.map[bestIdx] };
}

interface RecordedDemoPageProps {
  // Two routes render this same page — one showing the trend sparkline under
  // each Health Stats number, one with plain numbers like the page had
  // before, so both can be compared/linked to independently.
  showSparklines?: boolean;
}

const RecordedDemoPage: React.FC<RecordedDemoPageProps> = ({ showSparklines = true }) => {
  const navigate = useNavigate();
  const { demoId } = useParams<{ demoId?: string }>();
  const DATA_URL = demoId ? `/demo-replay/${demoId}/full_results.json` : DEFAULT_DATA_URL;
  const VIDEO_URL = demoId ? `/demo-replay/${demoId}/clip.mp4` : DEFAULT_VIDEO_URL;
  const GT_URL = demoId ? `/demo-replay/${demoId}/ground_truth.json` : null;
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const faceMeshRef = useRef<FaceMesh | null>(null);
  const faceMeshReinitializingRef = useRef(false);
  const handleFaceMeshResultsRef = useRef<(results: any) => void>(() => {});
  const isPlayingRef = useRef(false);
  const lastFaceMeshSendRef = useRef(0);
  const resultsRef = useRef<ReplayResult[]>([]);
  const appliedIndexRef = useRef(0);
  const groundTruthRef = useRef<GroundTruth | null>(null);
  // How much trailing ground-truth history to hand the charts each update —
  // comfortably more than any real response's new-sample count, so the
  // chart's own `.slice(-newCount)` always has enough to draw from.
  const GT_TAIL = 200;

  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [ppgSignal, setPpgSignal] = useState<number[]>([]);
  const [ecgSignal, setEcgSignal] = useState<number[]>([]);
  const [icgSignal, setIcgSignal] = useState<number[]>([]);
  const [bpSignal, setBpSignal] = useState<number[]>([]);
  const [groundTruthSignal, setGroundTruthSignal] = useState<{ ppg: number[]; ecg: number[]; icg: number[]; bp: number[] } | undefined>(undefined);
  const [gtMetrics, setGtMetrics] = useState<GroundTruthMetrics | undefined>(undefined);
  const [bufferFrames, setBufferFrames] = useState<number | undefined>(undefined);
  const [hr, setHr] = useState<number | null>(null);
  const [rr, setRr] = useState<number | null>(null);
  const [sbp, setSbp] = useState<number | null>(null);
  const [dbp, setDbp] = useState<number | null>(null);
  const [map, setMap] = useState<number | null>(null);
  const [spo2, setSpo2] = useState<number | null>(null);

  // Trend of each stat's own value over time, for the sparkline under its
  // number — not the raw PPG/ECG/etc. waveform (that's SignalsPanel's job).
  const HISTORY_LEN = 40;
  const [hrHistory, setHrHistory] = useState<number[]>([]);
  const [rrHistory, setRrHistory] = useState<number[]>([]);
  const [sbpHistory, setSbpHistory] = useState<number[]>([]);
  const [spo2History, setSpo2History] = useState<number[]>([]);
  const pushHistory = (setter: React.Dispatch<React.SetStateAction<number[]>>, value: number | null) => {
    if (value == null) return;
    setter((prev) => (prev.length >= HISTORY_LEN ? [...prev.slice(1), value] : [...prev, value]));
  };

  useEffect(() => {
    setLoaded(false);
    setLoadError('');
    resultsRef.current = [];
    groundTruthRef.current = null;
    resetReplay();

    const resultsPromise = fetch(DATA_URL).then((r) => {
      if (!r.ok) throw new Error(`${r.status}`);
      return r.json();
    });
    // No ground truth for the original reference-video demo (GT_URL is
    // null there) — only verification demos (:demoId present) have one.
    const gtPromise = GT_URL
      ? fetch(GT_URL).then((r) => (r.ok ? r.json() : null)).catch(() => null)
      : Promise.resolve(null);

    Promise.all([resultsPromise, gtPromise])
      .then(([data, gt]) => {
        resultsRef.current = data.results || [];
        groundTruthRef.current = gt;
        setLoaded(true);
      })
      .catch((e) => setLoadError(`Couldn't load recorded results: ${e}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DATA_URL, GT_URL]);

  // Live, in-browser face tracking over the recorded video — same MediaPipe
  // FaceMesh model the live app runs, just pointed at this <video> instead
  // of a webcam. Genuinely computed per frame as the video plays, not baked
  // in or precomputed, so the overlay tracks the actual face in the footage.
  const handleFaceMeshResults = useCallback((results: any) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !video || video.videoWidth === 0) return;

    const dispW = canvas.offsetWidth || video.videoWidth;
    const dispH = canvas.offsetHeight || video.videoHeight;
    canvas.width = dispW;
    canvas.height = dispH;

    const vW = video.videoWidth, vH = video.videoHeight;
    const cvScale = Math.max(dispW / vW, dispH / vH);
    const cvOffX = (dispW - vW * cvScale) / 2;
    const cvOffY = (dispH - vH * cvScale) / 2;

    ctx.clearRect(0, 0, dispW, dispH);

    const faceLandmarks = results.multiFaceLandmarks?.[0];
    if (!faceLandmarks) {
      drawFaceGuideOval(ctx, dispW, dispH, null);
      return;
    }

    const dispLandmarks = faceLandmarks.map((lm: any) => ({
      x: (lm.x * vW * cvScale + cvOffX) / dispW,
      y: (lm.y * vH * cvScale + cvOffY) / dispH,
    }));
    drawFaceGuideOval(ctx, dispW, dispH, dispLandmarks);
    const dotR = Math.max(1, Math.round(dispW * 0.004));
    ctx.save();
    ctx.fillStyle = 'rgba(96,165,250,0.55)';
    for (const dlm of dispLandmarks) {
      ctx.beginPath();
      ctx.arc(dlm.x * dispW, dlm.y * dispH, dotR, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }, []);
  handleFaceMeshResultsRef.current = handleFaceMeshResults;

  // Same init pattern WebcamCard uses — kept as a reusable function rather
  // than inline in a mount effect because sendFrameLoop below needs to call
  // it again if the WASM module crashes mid-playback.
  const initializeFaceMesh = useCallback(async () => {
    try {
      if (faceMeshRef.current) {
        try { faceMeshRef.current.close(); } catch { /* ignore */ }
        faceMeshRef.current = null;
      }
      const faceMesh = new FaceMesh({
        locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
      });
      faceMesh.setOptions({
        maxNumFaces: 1,
        refineLandmarks: false,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
      faceMesh.onResults((results) => {
        try { handleFaceMeshResultsRef.current(results); } catch (error) { console.error('Error handling FaceMesh results:', error); }
      });
      faceMeshRef.current = faceMesh;
    } catch (error) {
      console.error('Error initializing FaceMesh:', error);
    }
  }, []);

  useEffect(() => {
    initializeFaceMesh();
    return () => {
      const fm = faceMeshRef.current;
      faceMeshRef.current = null;
      try { fm?.close(); } catch { /* ignore */ }
    };
  }, [initializeFaceMesh]);

  // Mirrors WebcamCard's sendFrameToFaceMesh exactly: MediaPipe's WASM module
  // can abort mid-session (typically a slow/interrupted CDN load) — without
  // catching that and reinitializing, the overlay would silently stop
  // forever on the first such hiccup instead of recovering.
  const sendFrameLoop = useCallback(async () => {
    const video = videoRef.current;
    if (!isPlayingRef.current || !video) return;
    try {
      const now = performance.now();
      if (now >= lastFaceMeshSendRef.current + 33) {
        lastFaceMeshSendRef.current = now;
        if (video.readyState >= 2 && video.videoWidth > 0 && faceMeshRef.current) {
          await faceMeshRef.current.send({ image: video });
        }
      }
      if (isPlayingRef.current) requestAnimationFrame(() => void sendFrameLoop());
    } catch (error) {
      const msg = error instanceof Error ? error.message : '';
      if ((msg.includes('abort') || error instanceof (window as any).RuntimeError) && !faceMeshReinitializingRef.current) {
        faceMeshReinitializingRef.current = true;
        faceMeshRef.current = null;
        try {
          await initializeFaceMesh();
        } finally {
          faceMeshReinitializingRef.current = false;
        }
        if (isPlayingRef.current) requestAnimationFrame(() => void sendFrameLoop());
      } else if (!faceMeshReinitializingRef.current) {
        console.error('Error sending frame to FaceMesh:', error);
        if (isPlayingRef.current) requestAnimationFrame(() => void sendFrameLoop());
      }
    }
  }, [initializeFaceMesh]);

  const applyResult = (r: ReplayResult) => {
    setPpgSignal(r.PPG);
    setEcgSignal(r.ECG);
    setIcgSignal(r.ICG);
    setBpSignal(r.BP_wave);
    setBufferFrames(r.buffer_frames);
    // Round before display — the model's raw output is unrounded (e.g.
    // SBP 103.4), and every other page already rounds before setting state.
    // Left as-is here, the decimal made the "Blood Pressure" card's
    // "103.4/71.6" string overflow its fixed-size box.
    const sbpVal = r.SBP?.[0] != null ? Math.round(r.SBP[0]) : null;
    const dbpVal = r.DBP?.[0] != null ? Math.round(r.DBP[0]) : null;
    setHr(r.HR != null ? Math.round(r.HR) : null);
    setRr(r.RR != null ? Math.round(r.RR) : null);
    setSbp(sbpVal);
    setDbp(dbpVal);
    setMap(r.MAP != null ? Math.round(r.MAP) : null);
    setSpo2(r.SpO2 != null ? Math.round(r.SpO2) : null);
    pushHistory(setHrHistory, r.HR);
    pushHistory(setRrHistory, r.RR);
    pushHistory(setSbpHistory, sbpVal);
    pushHistory(setSpo2History, r.SpO2);

    const gt = groundTruthRef.current;
    if (gt) {
      const end = r.buffer_frames;
      const start = Math.max(0, end - GT_TAIL);
      setGroundTruthSignal({
        ppg: gt.ppg.slice(start, end),
        ecg: gt.ecg.slice(start, end),
        icg: gt.icg.slice(start, end),
        bp: gt.bp.slice(start, end),
      });
      if (gt.metrics_t && gt.metrics_series) {
        setGtMetrics(nearestMetrics(gt.metrics_t, gt.metrics_series, r._t));
      }
    }
  };

  const resetReplay = () => {
    appliedIndexRef.current = 0;
    setPpgSignal([]); setEcgSignal([]); setIcgSignal([]); setBpSignal([]);
    setBufferFrames(undefined);
    setHr(null); setRr(null); setSbp(null); setDbp(null); setMap(null); setSpo2(null);
    setHrHistory([]); setRrHistory([]); setSbpHistory([]); setSpo2History([]);
    setGroundTruthSignal(groundTruthRef.current ? { ppg: [], ecg: [], icg: [], bp: [] } : undefined);
    setGtMetrics(undefined);
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    const results = resultsRef.current;
    if (!video || results.length === 0) return;
    const t = video.currentTime;
    // Seeked backward past the last applied result — replay from the start
    // up to the new position instead of leaving stale-forward data on screen.
    if (appliedIndexRef.current > 0 && results[appliedIndexRef.current - 1]._t > t + 0.05) {
      resetReplay();
    }
    while (appliedIndexRef.current < results.length && results[appliedIndexRef.current]._t <= t) {
      applyResult(results[appliedIndexRef.current]);
      appliedIndexRef.current += 1;
    }
  };

  const handleVideoPlay = () => {
    isPlayingRef.current = true;
    setIsPlaying(true);
    requestAnimationFrame(() => void sendFrameLoop());
  };

  const handleVideoPause = () => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  // Mirrors MonitorPage's Start/Stop Monitoring button — here it drives
  // playback of the recorded video (and the replay of its precomputed
  // results) instead of starting a live webcam session.
  const handleStartClick = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
    } else {
      if (video.ended || video.currentTime >= video.duration - 0.05) {
        video.currentTime = 0;
        resetReplay();
      }
      video.play().catch(() => {});
    }
  };

  const hasData = hr != null;

  const computeWellness = () => {
    if (!hasData) return { score: 0, label: '—', color: '#60a5fa' };
    const hrScore = hr! >= 60 && hr! <= 80 ? 100 : hr! >= 50 && hr! <= 100 ? 80 : hr! >= 40 && hr! <= 120 ? 50 : 20;
    const spo2Score = spo2 != null ? (spo2 >= 98 ? 100 : spo2 >= 95 ? 75 : spo2 >= 90 ? 40 : 10) : 75;
    const hasBP = sbp != null && dbp != null;
    const bpScore = hasBP
      ? (() => {
          const s = sbp! < 120 ? 100 : sbp! < 130 ? 75 : sbp! < 140 ? 50 : 20;
          const d = dbp! < 80 ? 100 : dbp! < 90 ? 60 : 25;
          return (s + d) / 2;
        })()
      : 75;
    const rrScore = rr != null && rr >= 12 && rr <= 20 ? 100 : 60;
    // Same weighting as MonitorPage's wellness score, minus HRV (RhythmNet
    // doesn't output it), proportionally rescaled: 30/25/25/10 -> /0.9.
    const score = Math.round(Math.max(0, Math.min(100, hrScore * 0.333 + spo2Score * 0.278 + bpScore * 0.278 + rrScore * 0.111)));
    const label = score >= 85 ? 'Excellent' : score >= 70 ? 'Good' : score >= 50 ? 'Fair' : 'Low';
    const color = score >= 85 ? '#4ade80' : score >= 70 ? '#60a5fa' : score >= 50 ? '#fbbf24' : '#f87171';
    return { score, label, color };
  };
  const wellness = computeWellness();

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #0a1628 0%, #0d1f3c 60%, #0a1628 100%)',
      px: { xs: 1.5, md: 3, lg: 4 },
      py: { xs: 1.5, md: 2.5 },
      display: 'flex',
      flexDirection: 'column',
      gap: { xs: 1.5, md: 2 },
      maxWidth: 1600,
      mx: 'auto',
      width: '100%',
    }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate('/')}
          sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontSize: '0.82rem', fontWeight: 500, '&:hover': { color: 'white', bgcolor: 'transparent' } }}
        >
          Back
        </Button>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'rgba(255,255,255,0.85)', letterSpacing: '4px', textTransform: 'uppercase' }}>
          SeeVita
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 80, justifyContent: 'flex-end' }}>
          {isPlaying && (
            <>
              <Box sx={{
                width: 7, height: 7, borderRadius: '50%',
                bgcolor: hasData ? '#4ade80' : '#fbbf24',
                boxShadow: hasData ? '0 0 6px #4ade80' : '0 0 6px #fbbf24',
                animation: 'pulse 1.8s ease-in-out infinite',
                '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
              }} />
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: hasData ? '#4ade80' : '#fbbf24' }}>
                {hasData ? 'Live' : 'Collecting…'}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* Verification-demo index — shown only on the base /demo-replay route
          (no :demoId), so there's one link to hand people regardless of
          which specific demo they end up on. */}
      {!demoId && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' }}>
            Verification demos (real subjects, held out of training):
          </Typography>
          {[['brian-bfly', 'Brian'], ['khanh-bfly', 'Khanh']].map(([id, name]) => (
            <Button
              key={id}
              component={RouterLink}
              to={`/demo-replay/${id}`}
              size="small"
              sx={{ textTransform: 'none', fontSize: '0.72rem', color: '#60a5fa', fontWeight: 600, minWidth: 0, p: 0 }}
            >
              {name} →
            </Button>
          ))}
        </Box>
      )}

      {demoId && groundTruthRef.current && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' }}>
            {groundTruthRef.current.label ?? demoId} — vs. real lab-device ground truth
            {groundTruthRef.current.corr && (
              <>
                {' · corr '}
                {(['ppg', 'ecg', 'icg', 'bp'] as const)
                  .filter((k) => groundTruthRef.current!.corr?.[k] != null)
                  .map((k) => `${k.toUpperCase()} ${groundTruthRef.current!.corr![k]!.toFixed(2)}`)
                  .join(' · ')}
              </>
            )}
          </Typography>
        </Box>
      )}

      {loadError && (
        <Alert severity="warning" sx={{ borderRadius: '12px', py: 0.5 }}>
          {loadError}
        </Alert>
      )}

      {/* ── Main grid ── */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '360px 1fr', lg: '420px 1fr' },
        gap: { xs: 1.5, md: 2.5 },
        flex: 1,
        minHeight: 0,
        alignItems: 'start',
      }}>

        {/* ── Left column: recorded video + mediapipe overlay + start control ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ ...cardSx, p: 0, overflow: 'hidden', position: 'relative' }}>
            <Box sx={{ width: '100%', aspectRatio: { xs: '3 / 4', md: '1 / 1' }, overflow: 'hidden', borderRadius: '20px', position: 'relative' }}>
              <video
                ref={videoRef}
                src={VIDEO_URL}
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                onTimeUpdate={handleTimeUpdate}
                onSeeking={handleTimeUpdate}
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
                onEnded={handleVideoPause}
              />
              <canvas
                ref={canvasRef}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
              />
            </Box>
          </Box>

          <Button
            variant="contained"
            onClick={handleStartClick}
            disabled={!loaded}
            sx={{
              py: 1.4, borderRadius: '50px', fontSize: '0.88rem', fontWeight: 700, textTransform: 'none',
              bgcolor: isPlaying ? 'transparent' : 'white',
              color: isPlaying ? '#f87171' : '#0a1628',
              border: isPlaying ? '1.5px solid rgba(248,113,113,0.45)' : 'none',
              boxShadow: isPlaying ? 'none' : '0 4px 24px rgba(255,255,255,0.12)',
              transition: 'all 0.2s ease',
              '&:hover': { bgcolor: isPlaying ? 'rgba(239,68,68,0.08)' : '#e8f0fe', transform: 'translateY(-1px)' },
              '&:active': { transform: 'translateY(0)' },
              '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.18)', boxShadow: 'none', border: 'none' },
            }}
          >
            {!loaded ? 'Loading recording…' : isPlaying ? 'Pause Demo' : 'Start Demo'}
          </Button>
        </Box>

        {/* ── Right column: health stats + signals ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 }, minHeight: 0 }}>

          {/* Health cards — on top, per the requested layout */}
          <Box sx={cardSx}>
            <Typography sx={cardLabelSx}>Health Stats</Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1.2, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.07)', opacity: hasData ? 1 : 0.3, transition: 'opacity 0.5s ease', mb: 0.5 }}>
              <Box>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '2px', textTransform: 'uppercase' }}>Wellness Score</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', mt: 0.3 }}>
                  {hasData ? wellness.label : 'Press Start Demo to replay'}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '2.4rem', fontWeight: 900, color: wellness.color, lineHeight: 1 }}>
                {hasData ? wellness.score : '—'}
              </Typography>
            </Box>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(auto-fit, minmax(100px, 1fr))', md: 'repeat(auto-fit, minmax(120px, 1fr))' },
              gridAutoRows: '1fr',
              gap: { xs: 1, md: 1.5 },
              opacity: hasData ? 1 : 0.3,
              transition: 'opacity 0.5s ease',
            }}>
              <HealthCard title="Heart Rate" value={hr ?? '—'} unit="BPM" bgColor="rgba(239,68,68,0.15)" textColor="#f87171" history={showSparklines ? hrHistory : undefined} />
              <HealthCard title="Blood Pressure" value={sbp != null && dbp != null ? `${sbp}/${dbp}` : '—'} unit={map != null ? `mmHg · MAP ${map}` : 'mmHg'} bgColor="rgba(59,130,246,0.15)" textColor="#60a5fa" history={showSparklines ? sbpHistory : undefined} />
              <HealthCard title="Resp. Rate" value={rr ?? '—'} unit="/min" bgColor="rgba(20,184,166,0.15)" textColor="#2dd4bf" history={showSparklines ? rrHistory : undefined} />
              <HealthCard title="SpO2" value={spo2 ?? '—'} unit="%" bgColor="rgba(34,197,94,0.15)" textColor="#4ade80" history={showSparklines ? spo2History : undefined} />
            </Box>

            {/* Live, not static: a fixed clip-average number next to the
                model's own continuously-updating HR/BP wouldn't be a fair
                comparison, so this is a rolling window over ground truth
                (same window lengths as the model itself uses) looked up at
                the current playback time -- see nearestMetrics above and
                build_demo.py's compute_metrics_series. Dashed divider
                matches the chart overlay's own dashed ground-truth line. */}
            {groundTruthRef.current?.metrics_series && (
              <Box sx={{ mt: 1.2, pt: 1, borderTop: '1px dashed rgba(255,255,255,0.15)', opacity: hasData ? 1 : 0.3, transition: 'opacity 0.5s ease' }}>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '2px', textTransform: 'uppercase', mb: 0.6 }}>
                  Ground truth (reference device)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.65)' }}>
                    HR <b>{gtMetrics?.hr ?? '—'}</b> bpm
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.65)' }}>
                    BP <b>{gtMetrics?.sbp ?? '—'}/{gtMetrics?.dbp ?? '—'}</b> mmHg
                    {gtMetrics?.map != null && ` · MAP ${gtMetrics.map}`}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Signal charts — below the numbers, per the requested layout */}
          <Box sx={cardSx}>
            <Typography sx={cardLabelSx}>Signals</Typography>
            <SignalsPanel isMonitoring={isPlaying} ppgSignal={ppgSignal} ecgSignal={ecgSignal} icgSignal={icgSignal} bpSignal={bpSignal} bufferFrames={bufferFrames} groundTruth={groundTruthSignal} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const cardSx = {
  background: 'rgba(10, 28, 60, 0.6)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: '20px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.05)',
  p: { xs: 1.5, md: 2 },
  display: 'flex',
  flexDirection: 'column' as const,
};

const cardLabelSx = {
  fontSize: '0.68rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.35)',
  letterSpacing: '2.5px',
  textTransform: 'uppercase' as const,
  mb: { xs: 1, md: 1.5 },
};

export default RecordedDemoPage;
